import React from 'react';

import {
  Button,
  Group,
  useMantineTheme,
  useComputedColorScheme,
  Combobox,
  useCombobox,
  Text,
} from '@mantine/core';
import { BaseRecord } from '@refinedev/core';
import {
  IconChevronDown,
  IconNotebook,
  IconPlus,
  IconRepeat,
  IconStack2,
} from '@tabler/icons-react';
import { MRT_TableInstance } from 'mantine-react-table';
import { Dispatch, SetStateAction } from 'react';
import { useClaimDraftStore } from 'store/ClaimDraftStore';
import { buttonTextColor } from 'utils/buttonUtils';

import { isFeatureEnabled } from 'firebase';

type CreateModalType =
  | 'claim'
  | 'bulk'
  | 'repeat'
  | 'submissionFile';

interface ClaimCreateComboButtonProps<TData extends BaseRecord> {
  table: MRT_TableInstance<TData>;
  isLoading?: boolean;
  disabled?: boolean;
  setCreateModalType?: Dispatch<SetStateAction<string>>;
  variant?: 'filled' | 'outline' | 'light' | 'subtle';
  radius?: string | number;
}

export const ClaimCreateComboButton = <TData extends BaseRecord>({
  table,
  setCreateModalType,
  variant = 'outline',
  disabled = false,
  radius = 'md',
}: ClaimCreateComboButtonProps<TData>) => {
  const computedColorScheme = useComputedColorScheme('dark');
  const theme = useMantineTheme();
  const { getCreateDraft, getBulkDraft } = useClaimDraftStore();
  const hasClaimDraft = !!getCreateDraft();
  const hasBatchDraft = !!getBulkDraft();

  const ICON_SIZE = 40;

  const isDisabled = disabled;
  const createOptions: Array<{
    label: string;
    value: CreateModalType;
    icon: React.ReactNode;
    description?: string;
  }> = [
    {
      label: 'Claim',
      value: 'claim',
      description: 'Create a single new claim record.',
      icon: hasClaimDraft ? (
        <IconNotebook size={ICON_SIZE} />
      ) : (
        <IconPlus size={ICON_SIZE} />
      ),
    },
    {
      label: 'Batch',
      value: 'bulk',
      description: 'Create multiple claims for a single provider.',
      icon: hasBatchDraft ? (
        <IconNotebook size={ICON_SIZE} />
      ) : (
        <IconStack2 size={ICON_SIZE} />
      ),
    },
  ];

  if (isFeatureEnabled('repeat_claims')) {
    createOptions.push({
      label: 'Repeat Claims',
      value: 'repeat',
      description:
        'Create multiple claims spanning a date range with the same details.',
      icon: hasClaimDraft ? (
        <IconNotebook size={ICON_SIZE} />
      ) : (
        <IconRepeat size={ICON_SIZE} />
      ),
    });
  }

  const createOptionItems = createOptions.map((item) => (
    <Combobox.Option value={item.value} key={item.value}>
      <Combobox.Option value={item.value} key={item.value}>
        <Group gap="sm" justify="space-between" align="flex-start" w="100%">
          <Group gap="sm" wrap="nowrap">
            {item.icon}
            <div>
              <Text size="sm" fw={500}>
                {item.label}
              </Text>
              {item.description && (
                <Text size="xs" c="dimmed">
                  {item.description}
                </Text>
              )}
            </div>
          </Group>
        </Group>
      </Combobox.Option>
    </Combobox.Option>
  ));

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const handleCreate = (modalType: CreateModalType) => {
    if (setCreateModalType) {
      setCreateModalType(modalType);
    }
    table.setCreatingRow(true);
  };

  return (
    <Group gap={0} wrap="nowrap">
      <Combobox
        store={combobox}
        width={250}
        position="bottom-start"
        withArrow
        withinPortal
        onOptionSubmit={(value) => {
          const selectedOption = createOptions.find(
            (option) => option.value === value
          );
          if (selectedOption) {
            handleCreate(selectedOption.value);
          }
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <Button
            variant={variant}
            radius={radius}
            disabled={isDisabled}
            aria-label="Open claim creation options"
            data-testid="create-claim-button"
            c={
              variant === 'filled'
                ? 'white'
                : buttonTextColor(computedColorScheme, isDisabled)
            }
            onClick={() => combobox.toggleDropdown()}
            leftSection={<IconPlus size={16} />}
            rightSection={<IconChevronDown size={16} />}
            styles={
              variant === 'filled'
                ? {
                    root: {
                      background:
                        computedColorScheme === 'dark'
                          ? theme.colors.dark[4]
                          : theme.colors.dark[9],
                      borderColor:
                        computedColorScheme === 'dark'
                          ? theme.colors.dark[4]
                          : theme.colors.dark[9],
                    },
                  }
                : undefined
            }
          >
            Create
          </Button>
        </Combobox.Target>
        <Combobox.Dropdown p="xs">
          <Combobox.Options>{createOptionItems}</Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </Group>
  );
};
